import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge, Spinner, Button, Form, InputGroup } from "react-bootstrap";
import { FiDatabase, FiSearch, FiChevronLeft, FiChevronRight, FiExternalLink } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

// Define types for the assets API data
interface Asset {
  asset_type: string;
  asset_code?: string;
  asset_issuer?: string;
  paging_token?: string;
  num_claimable_balances?: number;
  num_liquidity_pools?: number;
  num_contracts?: number;
  accounts?: {
    authorized?: number;
    authorized_to_maintain_liabilities?: number;
    unauthorized?: number;
  };
  claimable_balances_amount?: string;
  liquidity_pools_amount?: string;
  contracts_amount?: string;
  balances?: {
    authorized?: string;
    authorized_to_maintain_liabilities?: string;
    unauthorized?: string;
  };
  flags?: {
    auth_required?: boolean;
    auth_revocable?: boolean;
    auth_immutable?: boolean;
    auth_clawback_enabled?: boolean;
  };
  _links?: {
    toml?: {
      href?: string;
    };
  };
}

interface AssetsApiResponse {
  _links: {
    self: { href: string };
    next?: { href: string };
    prev?: { href: string };
  };
  _embedded: {
    records: Asset[];
  };
}

const Assets: React.FC = () => {
  const [assetsData, setAssetsData] = useState<AssetsApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  useEffect(() => {
    setHeading(t('assets.heading'));
    setTitle(t('assets.title'));
    setDescription(t('assets.description'));
  }, [setHeading, setTitle, setDescription, t, language]);
  
  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (assetsData?._embedded?.records) {
      const filtered = assetsData._embedded.records.filter(asset =>
        (asset.asset_code?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (asset.asset_issuer?.toLowerCase() || '').includes(searchQuery.toLowerCase())
      );
      setFilteredAssets(filtered);
    }
  }, [assetsData, searchQuery]);

  const fetchAssets = async (url?: string) => {
    try {
      setLoading(true);
      const apiUrl = url || 'https://api.testnet.minepi.com/assets?limit=100';
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setAssetsData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch assets data: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (assetsData?._links?.next) {
      fetchAssets(assetsData._links.next.href);
    }
  };

  const handlePrevPage = () => {
    if (assetsData?._links?.prev) {
      fetchAssets(assetsData._links.prev.href);
    }
  };

  const formatNumber = (num: string): string => {
    if (!num || num === '') return '0';
    const number = parseFloat(num);
    if (isNaN(number)) return '0';
    return number.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 7
    });
  };

  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    if (num === 0) return "0";
    if (num < 0.0001) return "<0.0001";
    return formatNumber(balance);
  };

  const getTotalSupply = (asset: Asset): string => {
    const authorizedBalance = parseFloat(asset.balances?.authorized || '0');
    const liquidityPoolsAmount = parseFloat(asset.liquidity_pools_amount || '0');
    const total = authorizedBalance + liquidityPoolsAmount;
    
    if (total === 0) return "0";
    if (total < 0.0001) return "<0.0001";
    return formatNumber(total.toString());
  };

  const getPoolsAmount = (asset: Asset): string => {
    return formatBalance(asset.liquidity_pools_amount || '0');
  };

  const getCirculatingSupply = (asset: Asset): string => {
    return formatBalance(asset.balances?.authorized || '0');
  };

  const getAssetTypeColor = (type: string): string => {
    switch (type) {
      case 'credit_alphanum4':
        return 'primary';
      case 'credit_alphanum12':
        return 'success';
      case 'native':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getAssetTypeLabel = (type: string): string => {
    switch (type) {
      case 'credit_alphanum4':
        return 'Alpha4';
      case 'credit_alphanum12':
        return 'Alpha12';
      case 'native':
        return 'Native';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!assetsData) {
    return <div className="alert alert-warning">No assets data available</div>;
  }

  return (
    <div className="assets-page">
      {/* Header with Search */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex align-items-center mb-2 mb-md-0">
                  <div className="rounded-circle p-2 bg-primary bg-opacity-10 me-3">
                    {FiDatabase({size:24,className:'text-primary'})}
                  </div>
                  <div>
                    <h4 className="mb-1">{t('assets.total_assets')}</h4>
                    <p className="text-danger mb-0 small fw-medium">
                      {t('assets.testnet_warning')}
                    </p>
                  </div>
                </div>
                <div className="search-container" style={{ minWidth: '300px' }}>
                  <InputGroup>
                    <InputGroup.Text>
                      {FiSearch({size:16})}
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('assets.search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assets Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('assets.assets_list')}</h5>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!assetsData._links.prev}
                  onClick={handlePrevPage}
                >
                  {FiChevronLeft({size:16,className:'me-1'})}
                  {t('assets.previous')}
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!assetsData._links.next}
                  onClick={handleNextPage}
                >
                  {t('assets.next')}
                  {FiChevronRight({size:16,className:'ms-1'})}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('assets.asset_code')}</th>
                      <th>{t('assets.type')}</th>
                      <th>{t('assets.issuer')}</th>
                      <th className="text-end">{t('assets.holders')}</th>
                      <th className="text-end">{t('assets.total_supply')}</th>
                      <th className="text-end">{t('assets.in_pools')}</th>
                      <th className="text-end">{t('assets.circulating')}</th>
                      <th className="text-end">{t('assets.pools')}</th>
                      <th className="text-center" style={{minWidth: '180px'}}>{t('assets.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((asset, index) => (
                      <tr key={`${asset.asset_code}-${asset.asset_issuer}`}>
                        <td>
                          <div className="d-flex align-items-center">
                            <a 
                              href={`/asset/${encodeURIComponent(asset.asset_code + ':' + asset.asset_issuer)}`}
                              className="text-decoration-none"
                            >
                              <strong className="text-primary">{asset.asset_code}</strong>
                            </a>
                            {asset.flags?.auth_required && (
                              <Badge bg="warning" className="ms-2 small">Auth Required</Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge bg={getAssetTypeColor(asset.asset_type)}>
                            {getAssetTypeLabel(asset.asset_type)}
                          </Badge>
                        </td>
                        <td>
                          {asset.asset_issuer ? (
                            <a 
                              href={`/account/${asset.asset_issuer}`}
                              className="text-decoration-none"
                              title={asset.asset_issuer}
                            >
                              <code className="small text-primary">
                                {asset.asset_issuer.slice(0, 8)}...{asset.asset_issuer.slice(-8)}
                              </code>
                            </a>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div>
                            <span className="fw-medium">{formatNumber((asset.accounts?.authorized || 0).toString())}</span>
                            {(asset.accounts?.unauthorized || 0) > 0 && (
                              <div className="small text-warning">
                                +{formatNumber((asset.accounts?.unauthorized || 0).toString())} unauthorized
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-end">
                          <span className="fw-medium">{getTotalSupply(asset)}</span>
                        </td>
                        <td className="text-end">
                          <span className="fw-medium">{getPoolsAmount(asset)}</span>
                        </td>
                        <td className="text-end">
                          <span className="fw-medium">{getCirculatingSupply(asset)}</span>
                        </td>
                        <td className="text-end">
                          <span className="badge bg-light text-dark">
                            {asset.num_liquidity_pools}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-column gap-1 text-start">
                            {asset._links?.toml?.href ? (
                              <div>
                                <small className="text-muted d-block">TOML:</small>
                                <a 
                                  href={asset._links.toml.href} 
                                  target="_blank" 
                                  rel="nofollow noopener noreferrer"
                                  className="text-primary text-decoration-none small"
                                  style={{ wordBreak: 'break-all' }}
                                >
                                  {asset._links.toml.href.length > 30 
                                    ? `${asset._links.toml.href.slice(0, 30)}...` 
                                    : asset._links.toml.href}
                                  {FiExternalLink({size:12,className:'ms-1'})}
                                </a>
                              </div>
                            ) : (
                              <small className="text-muted">No TOML</small>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <small className="text-danger fw-medium">
                {t('assets.testnet_warning')}
              </small>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!assetsData._links.prev}
                  onClick={handlePrevPage}
                >
                  {FiChevronLeft({size:16,className:'me-1'})}
                  {t('assets.previous')}
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!assetsData._links.next}
                  onClick={handleNextPage}
                >
                  {t('assets.next')}
                  {FiChevronRight({size:16,className:'ms-1'})}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Assets;
